import { z } from "zod";
import type { PipelineNode, PipelineSpec, ToolCore } from "@loumy/contracts";
import { StudioSegment } from "@loumy/contracts";
import { CAP } from "@loumy/capabilities";

// §9.17/D139 — Studio-WebM-Export (Alpha-WebM-Output v1). INTERNES Tool: kein eigener Tool-Tab
// (nicht in UI_LOADERS) und NICHT in der Public-API-Allowlist (D46/D51, opt-in). Es bedient den
// dritten Studio-Output (WebM-Alpha = JOB; Sheet-/Einzelframe-PNG laufen über POST /v1/edits).
//
// Warum ein Job und nicht ein Browser-Edit: VP9-Alpha lässt sich im Browser nicht enkodieren
// (D132). Der Studio re-läuft daher VOM SOURCE-ASSET: decode → frames-select-segments (NEUE
// Capability, schneidet IDENTISCH zur Pyodide-Vorschau, SSOT ops.frames_select_segments) →
// optional chroma-key → video-encode (VP9-Alpha). Preis über den normalen Job-Hold/settle.
//
// v1-Grenze (§13): WebM-Export OHNE RIFE-Naht (die Naht-Glättung gibt es nur beim Sheet-PNG-Save)
// und nur für VIDEO-Quellen (Sheet/Bild-Quellen → §13). Der Studio bietet den Export daher nur an,
// wenn eine Source-Video-Asset existiert (eigenes Video oder das source-video eines Spritesheets).

const MAX_FPS = 60;

export const StudioExportSettings = z.object({
  /** Timeline-Segmente (halb-offen [from,to), geordnet = Output-Reihenfolge). Leer ⇒ ganzes Video. */
  segments: z.array(StudioSegment).default([]),
  /** decode-/encode-fps — MUSS dem Studio-Vorschau-fps entsprechen (gleiche Dauer-Semantik). */
  fps: z.number().int().positive().max(MAX_FPS).default(24),
  /** Hintergrund im WebM transparent keyen (chroma-key vor dem Encode). */
  removeBackground: z.boolean().default(false),
  tolerance: z.number().min(0).max(100).default(20),
  keyColor: z.enum(["auto", "green", "blue"]).default("auto"),
});
export type StudioExportSettings = z.infer<typeof StudioExportSettings>;

export const studioExportCore: ToolCore<StudioExportSettings> = {
  id: "studio-export",
  category: "video",
  // v1: Video-Quelle (eigenes Video bzw. das source-video eines Sheets). Sheet/Bild-Quellen = §13.
  inputs: [{ kind: "video", min: 1, max: 1, role: "source" }],
  settingsSchema: StudioExportSettings,
  outputSlots: [{ name: "video", kind: "video", optional: false }],
  buildPipeline(settings, _inputs, _ctx): PipelineSpec {
    const nodes: PipelineNode[] = [];
    // 1) Schnitt — decode (capability-intern, fps) → Segment-Auswahl. params sind SKALAR (D41):
    //    die Segment-Liste reist als eingefrorener JSON-STRING (D34) → deterministischer GPU-Key,
    //    identisch zur Browser-Vorschau; die Capability parst ihn zurück.
    nodes.push({
      id: "cut",
      capabilityId: CAP.framesSelectSegments.id,
      capabilityVersion: CAP.framesSelectSegments.version,
      surface: "server",
      inputs: { video: { node: "$input", port: "source" } },
      params: { segments: JSON.stringify(settings.segments), fps: settings.fps },
    });
    let framesNode = "cut";
    // 2) Optionaler chroma-key (transparenter WebM).
    if (settings.removeBackground) {
      nodes.push({
        id: "key",
        capabilityId: CAP.chromaKey.id,
        capabilityVersion: CAP.chromaKey.version,
        surface: "server",
        inputs: { frames: { node: framesNode, port: "frames" } },
        params: { tolerance: settings.tolerance, keyColor: settings.keyColor },
      });
      framesNode = "key";
    }
    // 3) Encode → transparentes WebM (VP9-Alpha). fps identisch zum Schnitt-decode (GPU-Key).
    nodes.push({
      id: "encode",
      capabilityId: CAP.videoEncode.id,
      capabilityVersion: CAP.videoEncode.version,
      surface: "server",
      inputs: { frames: { node: framesNode, port: "frames" } },
      params: { fps: settings.fps },
    });
    return { nodes, outputs: { video: { ref: { node: "encode", port: "video" } } } };
  },
};
